/**
 * Central access control for team pages.
 *
 * Each entry maps a route path prefix to the owner IDs whose members appear
 * on that page's roster. A user can see a team page if their owner ID is in
 * the list, OR if they have the "admin" role (Austin / Nick).
 *
 * Keep this in sync with the member lists in src/routes/_authenticated/teams.*.tsx.
 */
/** Human-readable label for each team page path, for display in access audits. */
export const TEAM_PAGE_LABELS: Record<string, string> = {
  "/teams/fp/development": "FP Team – Development",
  "/teams/fp/marketing": "FP Marketing",
  "/teams/fp/consultants": "FP Consultants",
  "/teams/fp/consultants/relational": "Relational Consultants",
  "/teams/fp/consultants/non-relational": "Non Relational Consultants",
  "/teams/fp/consultants/wedding-ring": "Wedding Ring Consultants",
  "/teams/fp/operations": "FP Team – Operations",
  "/teams/created/development": "Created Team – Development",
  "/teams/fpm": "Leadership Team",
  "/teams/fpm/production": "Stone Setting Team",
  "/teams/fpm/operation": "Metal Working Team",
  "/teams/fpm/development": "Quality Control Team",
};

const TEAM_PAGE_ACCESS: Record<string, string[]> = {
  // FP Team
  "/teams/fp/development": [
    "ff0b20a1-48c1-4d2d-a654-86768917de1a", // Austin Willard
    "cd17e640-8760-48b0-aec6-1fa31c973dec", // Nicholas Spalding
    "43404667-980f-4ec1-8900-0abfbfafd629", // Alex Sohn
    "216df4eb-25b9-46a4-ab19-fa7b13bef0de", // Jarrod Polson
  ],
  "/teams/fp/marketing": [
    "43404667-980f-4ec1-8900-0abfbfafd629", // Alex Sohn
    "7e18e77a-415d-4ddd-b8c4-92a7bb2af7d5", // Hope Holebrook
    "8cf396e3-2784-42dc-849f-2ce797eca145", // Alyssa Albornaz
  ],
  "/teams/fp/consultants": [
    "216df4eb-25b9-46a4-ab19-fa7b13bef0de", // Jarrod Polson
    "caac2ac4-8a98-4c7e-bd62-25ab132cbd55",
    "4102fc39-e7b1-4e65-b3a5-9d94ea8bd1d3",
  ],
  "/teams/fp/consultants/relational": [
    "caac2ac4-8a98-4c7e-bd62-25ab132cbd55",
    "054fb948-7256-4b16-825a-bde1aab6aefc",
    "5626061a-1643-465d-aa79-c142552f6a91",
    "35a6d0a4-f785-421b-8a00-a63b1b7b2c5b",
  ],
  "/teams/fp/consultants/non-relational": [
    "4102fc39-e7b1-4e65-b3a5-9d94ea8bd1d3",
    "e7b4176c-bd08-4c14-b566-78712b239fad",
    "b14ce866-6bb9-4362-b86f-75af78053a09",
  ],
  "/teams/fp/consultants/wedding-ring": ["70e6075c-d014-4f9e-8aa8-9babd56cc82d"],
  "/teams/fp/operations": [
    "f6edcd59-a0fa-4dd8-a8f0-1a49546f34ce",
    "e6d65da0-2e79-4560-a7cc-1e49e7d84d1f",
    "79eebf85-f675-4556-9262-6b8ec8fd13e8",
    "688e06fa-685f-40b5-b7ed-dc31cc752267",
  ],
  // Created Team
  "/teams/created/development": [
    "43404667-980f-4ec1-8900-0abfbfafd629", // Alex Sohn
    "f038fae9-5c46-4cc1-a5ea-9788166788ff", // Hope Damron
    "8cf396e3-2784-42dc-849f-2ce797eca145", // Alyssa Albornaz
  ],
  // FPM Team
  "/teams/fpm": ["3c0f82bb-7ad3-42b3-b26e-856623052748"],
  "/teams/fpm/production": [],
  "/teams/fpm/operation": [],
  "/teams/fpm/development": [],
};

/**
 * Guests: people who log in but aren't HubSpot owners (e.g. contractors), so
 * none of the owner-ID rules above can apply to them. Keyed by login email.
 * - teamPaths: team pages they may open (parents included automatically)
 * - ownerIds: individual dashboards they may open — Performance only; the PTO,
 *   Salary/Benefits and Reviews tabs stay closed to guests (server-enforced,
 *   those APIs never consult this list).
 */
const GUEST_ACCESS: Record<string, { teamPaths: string[]; ownerIds: string[] }> = {
  // Annelise Meador — independent contractor
  "annelisemeador@gmail.com": {
    teamPaths: [
      "/teams/fp/development",
      "/teams/fp/marketing",
      "/teams/fp/consultants/non-relational",
    ],
    ownerIds: ["b14ce866-6bb9-4362-b86f-75af78053a09"], // Mike Turner
  },
};

function guestFor(email: string | null | undefined) {
  return email ? GUEST_ACCESS[email.toLowerCase()] : undefined;
}

export function isGuest(email: string | null | undefined): boolean {
  return !!guestFor(email);
}

/** Owner IDs whose individual dashboard a guest may open. */
export function guestOwnerIds(email: string | null | undefined): string[] {
  return guestFor(email)?.ownerIds ?? [];
}

/**
 * Returns all team page paths a given owner ID (or guest email) is allowed to visit.
 * Parent paths are included automatically when a child is accessible
 * (e.g. if you can see /teams/fp/consultants/relational you can see /teams/fp/consultants).
 */
export function getAllowedTeamPaths(ownerId: string, email?: string | null): string[] {
  const allowed = new Set<string>();
  const add = (path: string) => {
    allowed.add(path);
    // Grant access to parent paths too
    const parts = path.split("/");
    for (let i = 2; i < parts.length; i++) {
      allowed.add(parts.slice(0, i + 1).join("/"));
    }
  };
  for (const [path, ids] of Object.entries(TEAM_PAGE_ACCESS)) {
    if (ownerId && ids.includes(ownerId)) add(path);
  }
  for (const path of guestFor(email)?.teamPaths ?? []) add(path);
  return [...allowed];
}

/** Returns true if the given path is a team page the owner can access. */
export function canAccessPath(ownerId: string, pathname: string, email?: string | null): boolean {
  // Staff: a granted path opens its whole subtree (and parent pages). Guests:
  // only the exact pages granted — a contractor granted one consultant
  // sub-team mustn't inherit its parent overview or the sibling sub-teams.
  const allowed = getAllowedTeamPaths(ownerId);
  const guestAllowed = !!guestFor(email)?.teamPaths.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
  return (
    pathname === "/my-dashboard" ||
    pathname === "/org-chart" ||
    // Individual dashboards enforce their own, finer-grained access: the route
    // loader checks /api/org-chart/can-view/:id (org-chart ancestor rule) and
    // redirects to /my-dashboard when denied, and the underlying APIs return 403.
    // This coarse layout guard must not block them, or managers can never open a
    // direct report's dashboard.
    pathname.startsWith("/owner-metrics/") ||
    pathname.startsWith("/owner-detail/") ||
    // Marketing Suite pages (/marketing/summary, /social, /web, /paid, /benchmarks)
    // enforce their own access server-side: every /api/marketing-suite endpoint
    // is gated by getDashboardSession(req, "marketing") (admin OR a marketing
    // dashboard assignment) and returns 403 otherwise, so users without access
    // see an empty page rather than data. This coarse layout guard must let them
    // through, or marketing-granted non-admins (e.g. Alex + team) get bounced to
    // /my-dashboard — same rationale as the individual-dashboard paths above.
    pathname.startsWith("/marketing/") ||
    // Consultant Hub pages — same pattern: /api/consultant-hub is gated by
    // getDashboardSession(req, "consultant").
    pathname.startsWith("/consultants/") ||
    guestAllowed ||
    allowed.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  );
}
